import {
  TOKEN_FIELDS,
  type TokenField,
  TokenTermsFields,
  type TokenTermsValues,
  readTokenTerms,
} from "@/components/billing/token-terms-fields";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/customers/:id/edit` — accountant/admin-only. The customer's
 * editable settings (name / fakturoidSubjectId) plus the danger-zone
 * delete, split off from the read-only customer profile at
 * `/customers/:id`. Since the 2026-05-20 Customer ↔ GitLab decouple
 * customers are Hub-only — no GitLab group path lives here anymore.
 */

type CustomerEdit = {
  id: string;
  name: string;
  fakturoidSubjectId: string | null;
  createdAt: string;
} & Record<TokenField, string | null>;

export const Route = createFileRoute("/_app/customers/$id/edit")({
  loader: async ({ params }) => {
    try {
      const [customer, company] = await Promise.all([
        api.get<CustomerEdit>(`/api/customers/${params.id}`),
        api
          .get<{ settings: TokenTermsValues | null }>("/api/accounting/settings")
          .then((r) => r.settings)
          .catch(() => null),
      ]);
      return { customer, company };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: CustomerEditPage,
});

const fieldClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

function CustomerEditPage() {
  const { t } = useTranslation();
  const { customer, company } = Route.useLoaderData();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {t("customers.edit.title", { name: customer.name })}
          </h1>
          <p className="text-muted-foreground mt-1">{t("customers.edit.subtitle")}</p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/customers/$id" params={{ id: customer.id }}>
            {t("customers.edit.backToProfile")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("customers.edit.infoTitle")}</CardTitle>
          <CardDescription>{t("customers.edit.infoHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <EditCustomerForm customer={customer} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("netlionTokens.customerTitle")}</CardTitle>
          <CardDescription>{t("netlionTokens.customerHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <TokenTermsForm customer={customer} company={company} />
        </CardContent>
      </Card>

      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive">{t("customers.edit.dangerTitle")}</CardTitle>
          <CardDescription>{t("customers.edit.dangerHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DeleteCustomerForm customerId={customer.id} />
        </CardContent>
      </Card>
    </div>
  );
}

function EditCustomerForm({ customer }: { customer: CustomerEdit }) {
  const { t } = useTranslation();
  const router = useRouter();
  const navigate = useNavigate();
  const [name, setName] = useState(customer.name);
  const [fakturoidSubjectId, setFakturoidSubjectId] = useState(customer.fakturoidSubjectId ?? "");
  const save = useApiMutation(
    (body: { name: string; fakturoidSubjectId: string | undefined }) =>
      api.patch(`/api/customers/${customer.id}`, body),
    {
      onSuccess: () => {
        toast({ title: t("customers.edit.updated"), variant: "success" });
        router.invalidate();
        navigate({ to: "/customers/$id", params: { id: customer.id } });
      },
      onError: (msg) =>
        toast({
          title: t("customers.edit.updateFailed"),
          description: msg,
          variant: "destructive",
        }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (save.pending) return;
    if (!name.trim()) {
      toast({ title: t("customers.edit.nameRequired"), variant: "destructive" });
      return;
    }
    await save.mutate({
      name: name.trim(),
      fakturoidSubjectId: fakturoidSubjectId.trim() || undefined,
    });
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="name" className="text-sm font-medium leading-none">
          {t("customers.edit.name")} <span className="text-destructive">*</span>
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="fakturoidSubjectId" className="text-sm font-medium leading-none">
          {t("customers.edit.fakturoidSubjectId")}
        </label>
        <input
          id="fakturoidSubjectId"
          type="number"
          value={fakturoidSubjectId}
          onChange={(e) => setFakturoidSubjectId(e.target.value)}
          className={fieldClass}
        />
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" disabled={save.pending}>
          {save.pending ? (
            <LoadingLabel text={t("customers.edit.saving")} />
          ) : (
            t("customers.edit.save")
          )}
        </Button>
        <span className="text-sm text-muted-foreground">
          {t("customers.edit.created")} <LocalDate value={customer.createdAt} />
        </span>
      </div>
    </form>
  );
}

function TokenTermsForm({
  customer,
  company,
}: {
  customer: CustomerEdit;
  company: TokenTermsValues | null;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const save = useApiMutation(
    (body: Record<TokenField, number | null>) => api.patch(`/api/customers/${customer.id}`, body),
    {
      onSuccess: () => {
        toast({ title: t("customers.edit.updated"), variant: "success" });
        router.invalidate();
      },
      onError: (msg) =>
        toast({
          title: t("customers.edit.updateFailed"),
          description: msg,
          variant: "destructive",
        }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (save.pending) return;
    await save.mutate(readTokenTerms(new FormData(e.currentTarget)));
  }

  const values = Object.fromEntries(TOKEN_FIELDS.map((f) => [f, customer[f]])) as TokenTermsValues;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="customer-token-terms">
      <TokenTermsFields values={values} placeholders={company ?? undefined} />
      <Button type="submit" disabled={save.pending}>
        {save.pending ? (
          <LoadingLabel text={t("customers.edit.saving")} />
        ) : (
          t("customers.edit.save")
        )}
      </Button>
    </form>
  );
}

function DeleteCustomerForm({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState("");
  const remove = useApiMutation(() => api.delete(`/api/customers/${customerId}`), {
    onSuccess: () => {
      toast({ title: t("customers.edit.deleted"), variant: "success" });
      navigate({ to: "/customers" });
    },
    // 409 when projects still reference the customer — the server's
    // friendly "reassign first" message rides through here (the hook's
    // default extraction surfaces it).
    onError: (msg) =>
      toast({ title: t("customers.edit.deleteFailed"), description: msg, variant: "destructive" }),
  });

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (remove.pending) return;
    if (confirm !== "DELETE") {
      toast({ title: t("customers.edit.typeDelete"), variant: "destructive" });
      return;
    }
    await remove.mutate();
  }

  return (
    <form onSubmit={submit} className="flex items-end gap-3">
      <div className="space-y-2">
        <label htmlFor="confirm" className="text-sm font-medium leading-none">
          {t("customers.edit.typeDelete")}
        </label>
        <input
          id="confirm"
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="DELETE"
          className={fieldClass}
        />
      </div>
      <Button type="submit" variant="destructive" disabled={remove.pending}>
        {remove.pending ? (
          <LoadingLabel text={t("customers.edit.deleting")} />
        ) : (
          t("customers.edit.delete")
        )}
      </Button>
    </form>
  );
}
